import { computed, signal } from '@preact/signals';
import { STARTER_LOADOUT } from '@vm/content';
import type {
  Achievement,
  BattlePassState,
  CatalogEntry,
  CatalogResponse,
  ChatMessage,
  ClanDetail,
  Cosmetics,
  FriendsResponse,
  InboxMessage,
  ItemType,
  Loadout,
  MatchAssignment,
  MatchRewards,
  MeResponse,
  ModeId,
  Party,
  Presence,
  QuestsResponse,
  Wallet,
} from '@vm/shared';
import type { ProfilePatch } from '../api/routes';

// Connection ----------------------------------------------------------------------------------------

/** 'online' = API reachable and signed in; 'offline' = local profile, online features disabled. */
export const conn = signal<'connecting' | 'online' | 'offline'>('connecting');
export const isOffline = computed(() => conn.value === 'offline');
export const onlineCount = signal<number | null>(null);

// Profile & economy -----------------------------------------------------------------------------------

export const me = signal<MeResponse | null>(null);
export const catalog = signal<CatalogResponse | null>(null);

export const DEFAULT_COSMETICS: Cosmetics = { skin: 'preset:marine', armor: 'standard', camos: {}, tracers: {}, scopes: {}, deathFx: 'shatter', banner: 'recruit' };

export const wallet = computed<Wallet>(() => me.value?.wallet ?? { coins: 0, gems: 0 });
export const inventory = computed(() => me.value?.inventory ?? []);
export const ownedKeys = computed(() => new Set(inventory.value.map((i) => `${i.type}:${i.id}`)));
/** Current loadout. Profiles from before pets (no `pet` key) have the starter pet, like on the server. */
export const loadout = computed<Loadout>(() => {
  const l = me.value?.loadout;
  if (!l) return { weapons: [...STARTER_LOADOUT.weapons], gadget: STARTER_LOADOUT.gadget, pet: STARTER_LOADOUT.pet };
  return l.pet === undefined ? { ...l, pet: STARTER_LOADOUT.pet } : l;
});
export const cosmetics = computed<Cosmetics>(() => ({ ...DEFAULT_COSMETICS, ...(me.value?.cosmetics ?? {}) }));
export const level = computed(() => me.value?.progression.level ?? 1);
export const restricted = computed(() => me.value?.restricted ?? false);

export const catalogIndex = computed(() => {
  const m = new Map<string, CatalogEntry>();
  for (const e of catalog.value?.items ?? []) m.set(`${e.type}:${e.id}`, e);
  return m;
});

export function catalogEntry(type: ItemType, id: string): CatalogEntry | undefined {
  return catalogIndex.value.get(`${type}:${id}`);
}

export function owns(type: ItemType, id: string): boolean {
  if (ownedKeys.value.has(`${type}:${id}`)) return true;
  const e = catalogEntry(type, id);
  return e?.source === 'starter';
}

/** Merges fields of a mutation response into the profile (a full MeResponse replaces it). */
export function patchMe(p: ProfilePatch | Partial<MeResponse> | null | undefined): void {
  if (!p || typeof p !== 'object') return;
  const full = (p as ProfilePatch).me ?? p;
  if ('id' in full && 'progression' in full && 'inventory' in full && 'wallet' in full) {
    me.value = full as MeResponse;
    return;
  }
  const cur = me.value;
  if (!cur) return;
  const next: MeResponse = { ...cur };
  const keys = ['wallet', 'inventory', 'loadout', 'cosmetics', 'progression', 'customSkins', 'unreadInbox', 'name', 'renamed', 'tutorialDone', 'stats', 'clan', 'dailyLogin', 'mutedUntil', 'email', 'isGuest'] as const;
  let changed = false;
  for (const k of keys) {
    const v = (p as Record<string, unknown>)[k];
    if (v !== undefined) {
      (next as unknown as Record<string, unknown>)[k] = v;
      changed = true;
    }
  }
  if (changed) me.value = next;
}

export function setWallet(w: Wallet | undefined): void {
  if (w && me.value) me.value = { ...me.value, wallet: w };
}

// Progression / live-ops ---------------------------------------------------------------------------------

export const quests = signal<QuestsResponse | null>(null);
export const battlePass = signal<BattlePassState | null>(null);
export const achievements = signal<Achievement[] | null>(null);

export const claimableQuests = computed(() => {
  const q = quests.value;
  if (!q) return 0;
  return [...q.daily, ...q.weekly].filter((x) => x.completed && !x.claimed).length;
});

export const claimableTiers = computed(() => {
  const bp = battlePass.value;
  if (!bp) return 0;
  const claimed = new Set(bp.claimed.map((c) => `${c.tier}:${c.track}`));
  let n = 0;
  for (const r of bp.season.rewards) {
    if (r.tier > bp.tier) continue;
    if (r.free && !claimed.has(`${r.tier}:free`)) n++;
    if (bp.premium && r.premium && !claimed.has(`${r.tier}:premium`)) n++;
  }
  return n;
});

export const claimableAchievements = computed(() => (achievements.value ?? []).filter((a) => a.unlocked && !a.claimed).length);

// Social -------------------------------------------------------------------------------------------------

export const friends = signal<FriendsResponse | null>(null);
/** Live presence pushed over the social socket, overriding the last friends fetch. */
export const presenceById = signal<Record<string, { presence: Presence; activity?: string }>>({});
export const party = signal<Party | null>(null);
export const myClan = signal<ClanDetail | null>(null);
/** Users muted locally in chat (never sent to the server). */
export const mutedUsers = signal<Set<string>>(new Set());

export const onlineFriends = computed(() => {
  const f = friends.value?.friends ?? [];
  const live = presenceById.value;
  return f.filter((x) => (live[x.id]?.presence ?? x.presence) !== 'offline').length;
});

export type ChannelKey = string;
export const chatLog = signal<Record<ChannelKey, ChatMessage[]>>({});
export const chatUnread = signal<Record<ChannelKey, number>>({});
export const chatOpen = signal(false);
export const chatChannel = signal<ChannelKey>('global');

export const inbox = signal<InboxMessage[] | null>(null);
export const unreadInbox = computed(() => me.value?.unreadInbox ?? 0);

// Matchmaking ---------------------------------------------------------------------------------------------

export type QueueState =
  | { state: 'idle' }
  | { state: 'searching'; mode: ModeId; mapId?: string; since: number; estimate: number; queueId?: string }
  | { state: 'joining'; mode: ModeId };

export const queue = signal<QueueState>({ state: 'idle' });
export const lastRewards = signal<MatchRewards | null>(null);
/** The online match currently being played (or last played). */
export const currentMatch = signal<{ match: MatchAssignment; ended: boolean; startedAt: number } | null>(null);

// Toasts ---------------------------------------------------------------------------------------------------

export type ToastKind = 'info' | 'success' | 'error' | 'warning' | 'reward' | 'social';

export interface ToastAction {
  label: string;
  primary?: boolean;
  onClick: () => void | Promise<void>;
}

export interface Toast {
  id: number;
  kind: ToastKind;
  title: string;
  body?: string;
  actions?: ToastAction[];
  ttl: number;
}

export const toasts = signal<Toast[]>([]);
let toastSeq = 1;

export function toast(title: string, opts: { kind?: ToastKind; body?: string; actions?: ToastAction[]; ttl?: number } = {}): number {
  const t: Toast = { id: toastSeq++, kind: opts.kind ?? 'info', title, body: opts.body, actions: opts.actions, ttl: opts.ttl ?? (opts.actions ? 12000 : 3800) };
  toasts.value = [...toasts.value.filter((x) => x.title !== title || x.body !== opts.body).slice(-3), t];
  if (t.ttl > 0) setTimeout(() => dismissToast(t.id), t.ttl);
  return t.id;
}

export function dismissToast(id: number): void {
  toasts.value = toasts.value.filter((t) => t.id !== id);
}
